// TODO: update
// @ts-nocheck

import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import '@/assets/scss/app.scss';
// F-34 (R-45): Plus Jakarta Sans is self-hosted — no request to Google for the checkout or BMW docs.
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/plus-jakarta-sans/800.css';
// Portal shell fonts, self-hosted (formerly loaded from fonts.googleapis.com in index.html —
// removed so no visitor IP reaches Google for fonts). Same weights/styles as before.
import '@fontsource/rubik/400.css';
import '@fontsource/rubik/400-italic.css';
import '@fontsource/rubik/500.css';
import '@fontsource/rubik/500-italic.css';
import '@fontsource/rubik/700.css';
import '@fontsource/rubik/700-italic.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/300-italic.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/500-italic.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/700-italic.css';
import '@fontsource/roboto/900.css';
import '@fontsource/roboto/900-italic.css';
import VueFeather from 'vue-feather';
import store from './store';
import VueApexCharts from 'vue3-apexcharts';
import DropZone from 'dropzone-vue';
import 'bootstrap/dist/js/bootstrap.bundle';
import PerfectScrollbar from 'vue3-perfect-scrollbar';
import 'vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css';
import Vue3Tour from 'vue3-tour';
import 'vue3-tour/dist/vue3-tour.css';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import VueAnimateOnScroll from 'vue3-animate-onscroll';
import { quillEditor } from 'vue3-quill';
import Lightbox from 'vue-easy-lightbox';
import { VueMasonryPlugin } from 'vue-masonry';
import vue3StarRatings from 'vue3-star-ratings';
import Datepicker from 'vue3-datepicker';
import VueChartist from 'vue-chartist';
import VueNumber from 'vue-number-animation';
import SimpleTypeahead from 'vue3-simple-typeahead';
import 'vue3-simple-typeahead/dist/vue3-simple-typeahead.css';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import 'swagger-ui/dist/swagger-ui.css';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import { enforceAuthRules } from '@/services/firebaseAuthService';
import { syncTabWithAuthUser } from '@/utils/authSessionSync';
import analyticsService from '@/services/analyticsService';
import {
  setupAnalyticsRouterTracking,
  setupGlobalErrorTracking,
  analyticsMixin,
} from '@/plugins/analyticsPlugin';
import AnalyticsDebugger from '@/utils/analyticsDebugger';
import i18n from '@/i18n';
import { setupTranslationNavigationGuards } from '@/i18n/setupNavigationGuards';
import { isPublicPath } from '@/utils/publicRoutes';

const app = createApp(App);

app.use(Toast, {position: 'bottom-right',});

app.use(SimpleTypeahead);
app.use(VueMasonryPlugin);
app.use(VueNumber);
app.component('vue3-star-ratings', vue3StarRatings);
app.use(quillEditor);
app.use(Lightbox);
app.use(store);
app.use(VueSweetalert2);
app.use(VueAnimateOnScroll);
app.use(Vue3Tour);
app.use(router);
app.use(i18n);
app.use(PerfectScrollbar);
app.use(DropZone);
app.component('multiselect', Multiselect);
app.component(VueFeather.name, VueFeather);
app.component('apexchart', VueApexCharts);
app.use(VueChartist.default ?? VueChartist);
app.component('Datepicker', Datepicker);
app.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyCMG1KGtL6MYvT1ypk8X29GygIOJpNZApw',
  },
});

// Setup translation navigation guards after router is available
setupTranslationNavigationGuards(router);

// Add analytics mixin globally
app.mixin(analyticsMixin);

// Setup global error tracking
setupGlobalErrorTracking(app);

// Setup router tracking
setupAnalyticsRouterTracking(router);

// Start monitoring auth state
enforceAuthRules();
syncTabWithAuthUser();

// Initialize analytics service.
// Skipped entirely on the public /promo/ landing pages and the public
// /checkout/ pages: Firebase Analytics persists an app-instance id to the
// device, and those pages are served to anonymous visitors without a cookie
// banner. They report through cookieless gtag instead (Consent Mode defaults
// are set in index.html).
if (import.meta.env.VITE_ENABLE_TRACKING === 'true' && !isPublicPath()) {
  console.log('🚀 Analytics tracking enabled - initializing...');

  // Wait for gtag to be available
  const initAnalytics = () => {
    if (window.gtag) {
      analyticsService.init();
      console.log('✅ Analytics service initialized successfully');

      // Run safe diagnostic in development mode (no events sent)
      if (import.meta.env.DEV) {
        setTimeout(() => {
          AnalyticsDebugger.runSafeDiagnostic();
        }, 2000);
      }
    } else {
      setTimeout(initAnalytics, 100);
    }
  };
  initAnalytics();

  // Make analytics service and debugger available globally for testing
  window.analyticsService = analyticsService;
  window.AnalyticsDebugger = AnalyticsDebugger;
} else {
  console.log('⚠️ Analytics tracking disabled (VUE_APP_ENABLE_TRACKING=false)');
}

async function bootstrap() {
  // Dev-only: mock the Pay-by-Invoice journey API (BE-1) until it ships.
  // Enable by setting VITE_ENABLE_PBI_MOCK=true (e.g. in .env.development.local).
  if (import.meta.env.VITE_ENABLE_PBI_MOCK === 'true') {
    const { enablePayByInvoiceMocks } = await import('@/mocks');
    await enablePayByInvoiceMocks();
  }
  app.mount('#app');
}

void bootstrap();
